import React, { useState, useContext, useEffect } from "react";
import { Link } from "react-router-dom";
import ReactHtmlParser from "react-html-parser";
import { AuthContext } from "../../context/AuthContext";
import JobFilterForm from "../../component/JobFilterForm";
import InfiniteScroll from "react-infinite-scroller";
import JobItem from "../../component/JobItem";
import { generateRandomString } from "../../Utility/Utility";

export default function Index() {
  const [aboutArray, setAboutArray] = useState([]);
  const [organizationArray, setOrganizationArray] = useState([]);
  const [homejobsArray, setHomejobsArray] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const { isAuthenticated, userType } = useContext(AuthContext);
  const [paginationData, setPaginationData] = useState({
    current_page: 0,
    from: 0,
    to: 0,
    per_page: 0,
    last_page: 0,
    total: 0,
    first_page_url: "",
    prev_page_url: "",
    last_page_url: "",
    next_page_url: "",
    path: "",
  });

  const [searchParams, setSearchParams] = useState({});
  const [showSeeMore, setShowSeeMore] = useState(false);
  const [isScrollVisible, setIsScrollVisible] = useState(false);

  // ─── Effect 1: Fetch frontend static data (about, organization) ───────────
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    axios
      .get(constvar.api_url + "home/get_frontend_data", {
        signal: controller.signal,
      })
      .then((response) => {
        if (!isMounted) return;
        if (response.data.status == "success") {
          setAboutArray(response.data.about);
          setOrganizationArray(response.data.organization);
        } else {
          setAboutArray([]);
          setOrganizationArray([]);
        }
      })
      .catch((err) => {
        if (!isMounted) return;
        if (axios.isCancel(err)) return; // request was aborted, ignore
        setAboutArray([]);
        setOrganizationArray([]);
      });

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  const setParams = (sParams) => {
    setSearchParams(sParams);
  };

  // ─── Effect 2: Fetch jobs when searchParams changes ────────────────────────
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchJobs = async () => {
      try {
        var query = new URLSearchParams(searchParams);
        var url = constvar.api_url + "jobs?" + query.toString();
        const response = await axios.get(url, { signal: controller.signal });

        if (!isMounted) return;

        if (response.data.status == "success") {
          var result = response.data.data;
          setHomejobsArray(result.data);
          delete result.data;
          setPaginationData({ ...result });
          setTotalRecord(result.total);

          if (result.next_page_url === undefined || result.next_page_url === null) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        } else {
          setHomejobsArray([]);
          setTotalRecord(0);
          setHasMore(false);
        }
      } catch (err) {
        if (!isMounted) return;
        if (axios.isCancel(err)) return; // aborted, ignore
        setHomejobsArray([]);
        setTotalRecord(0);
        setHasMore(false);
      }
    };

    fetchJobs();

    return () => {
      isMounted = false;
      controller.abort();
      setHomejobsArray([]);
      setTotalRecord(0);
    };
  }, [searchParams]);

  // ─── Fetch more jobs (infinite scroll) ────────────────────────────────────
  // Note: fetchMoreJobs is called by InfiniteScroll on demand (not inside useEffect),
  // so we don't need AbortController here, but we still guard with a local flag.
  const fetchMoreJobs = async () => {
    if (!hasMore) return false;

    if (paginationData.next_page_url === undefined || paginationData.next_page_url === null) {
      setHasMore(false);
      return false;
    }

    const parsedUrl = new URL(paginationData.next_page_url);
    let nextPage = parsedUrl.searchParams.get("page");
    if (nextPage === null) {
      setHasMore(false);
      return false;
    }

    let query = new URLSearchParams(searchParams);
    let url = constvar.api_url + "jobs?" + query.toString() + "&page=" + nextPage;

    try {
      const response = await axios.get(url);

      if (response.data.status == "success") {
        var result = response.data.data;

        if (result.data.length === 0) {
          setHasMore(false);
        } else {
          if (result.next_page_url === undefined || result.next_page_url === null) {
            setHasMore(false);
          } else {
            setHasMore(true);
          }
        }

        const updatedJobs = [...homejobsArray, ...result.data];
        setHomejobsArray(updatedJobs);

        if (updatedJobs.length >= 50) {
          setHasMore(false);
          setShowSeeMore(true);
        }

        delete result.data;
        setPaginationData({ ...result });
        setTotalRecord(result.total);
      } else {
        setHomejobsArray([]);
        setTotalRecord(0);
        setHasMore(false);
      }
    } catch (err) {
      // handle silently or show error state
    }
  };

  // ─── See More (manual load after 50 jobs) ─────────────────────────────────
  const fetchSeeMoreJobs = async () => {
    if (paginationData.next_page_url === undefined || paginationData.next_page_url === null) {
      setShowSeeMore(false);
      return;
    }

    const parsedUrl = new URL(paginationData.next_page_url);
    let nextPage = parsedUrl.searchParams.get("page");

    let query = new URLSearchParams(searchParams);
    let url = constvar.api_url + "jobs?" + query.toString() + "&page=" + nextPage;

    try {
      const response = await axios.get(url);

      if (response.data.status == "success") {
        var result = response.data.data;

        if (result.data.length === 0) {
          setShowSeeMore(false);
        }

        setHomejobsArray((prev) => [...prev, ...result.data]);
        delete result.data;
        setPaginationData({ ...result });
        setTotalRecord(result.total);
      } else {
        setShowSeeMore(false);
      }
    } catch (err) {
      setShowSeeMore(false);
    }
  };

  // ─── Effect 3: Scroll-to-top button visibility ────────────────────────────
  useEffect(() => {
    let isMounted = true;

    const toggleVisibility = () => {
      if (!isMounted) return;
      if (window.scrollY > 300) {
        setIsScrollVisible(true);
      } else {
        setIsScrollVisible(false);
      }
    };

    window.addEventListener("scroll", toggleVisibility);

    return () => {
      isMounted = false;
      window.removeEventListener("scroll", toggleVisibility);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <React.Fragment>
      <div className="section-full p-t120  p-b60 site-bg-light">
        <div className="container">
          <div className="row">
            <div className="col-xl-3 col-lg-4 col-md-12 rightSidebar">
              <div className="side-bar">
                <div className="sidebar-elements search-bx">
                  <JobFilterForm searchParams={searchParams} setParams={setParams} />
                </div>
              </div>

              <div className="twm-advertisment">
                <div className="overlay"></div>
                <h4 className="twm-title">Recruiting?</h4>
                <p>
                  Get Best Matched Jobs On your Email. <br />
                  Add Resume NOW !
                </p>
              </div>

              <div className="bigyapan">
                <Link to="/">
                  <figure>
                    <img src="portal/images/interniship.png" alt="#" />
                  </figure>
                </Link>
              </div>
            </div>

            <div className="col-xl-9 col-lg-8 col-md-12">
              <InfiniteScroll
                  dataLength={homejobsArray.length}
                  loadMore={fetchMoreJobs}
                  hasMore={hasMore}
                  loader={<h4>Loading...</h4>}
                >
                <div className="row" key={"jobListDiv"}>
                  {homejobsArray.length > 0
                    ? homejobsArray.map((jobitem) => {
                        let rnd = generateRandomString(8);
                        return (
                          <div className="col-lg-6 col-md-12 m-b30" key={`jobList${rnd}`}>
                            <JobItem job={jobitem} />
                          </div>
                        );
                      })
                    : ""}
                  <div className="clearfix"></div>
                </div>
              </InfiniteScroll>

              {showSeeMore && (
                <div className="text-center mt-0">
                  <button className="btn btn-primary" onClick={fetchSeeMoreJobs}>
                    See More jobs
                  </button>
                </div>
              )}

              {!hasMore && !showSeeMore && homejobsArray.length > 0 && (
                <div className="text-center mt-0">
                  <p className="text-muted"> No more jobs available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="section-full site-bg-light-purple twm-for-employee-4">
        <div className="container">
          <div className="section-content">
            <div className="twm-for-employee-content">
              <div className="row">
                {aboutArray.length > 0
                  ? aboutArray.map((item, key) => (
                      <div className="col-xl-5 col-lg-12 col-md-12" key={key}>
                        <div className="twm-explore-content-outer2">
                          <div className="twm-explore-top-section">
                            <div className="section-head left wt-small-separator-outer">
                              <div className="wt-small-separator site-text-primary">
                                <div>About </div>
                              </div>
                              <h2>{item.page_title}</h2>
                              <p>{item.short_content}</p>
                            </div>
                            <div className="twm-read-more">
                              <Link to={`/about`} className="site-button">
                                Read More
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  : ""}
                <div className="col-xl-7 col-lg-12 col-md-12">
                  <div className="twm-explore-right-section">
                    <div className="twm-media">
                      <div className="twm-employee-pic">
                        <img src="portal/images/sangita.png" alt="" />
                      </div>
                    </div>
                    {organizationArray.length > 0
                      ? organizationArray.map((item, key) => (
                          <div key={key}>
                            <div className="counter-outer-two one anm" data-speed-y="-2" data-speed-scale="15" data-speed-opacity="1">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-purple">
                                  <span className="counter">{item.total_jobseeker}</span>
                                </div>
                                <p className="icon-content-info">Total Jobseeker</p>
                              </div>
                            </div>
                            <div className="counter-outer-two two anm" data-speed-y="2" data-speed-scale="15" data-speed-opacity="5">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-sky">
                                  <span className="counter">{item.total_jobs}</span>
                                </div>
                                <p className="icon-content-info">Total job posted</p>
                              </div>
                            </div>
                            <div className="counter-outer-two three anm" data-speed-x="-4" data-speed-scale="-25">
                              <div className="icon-content">
                                <div className="tw-count-number text-clr-pink">
                                  <span className="counter">{item.total_employer}</span>
                                </div>
                                <p className="icon-content-info">Total employee</p>
                              </div>
                            </div>
                          </div>
                        ))
                      : ""}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isScrollVisible && (
        <button
          className="scroltop"
          onClick={scrollToTop}
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            backgroundColor: "#6c63ff",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "45px",
            height: "45px",
            fontSize: "20px",
            cursor: "pointer",
            boxShadow: "0px 4px 8px rgba(0,0,0,0.2)",
            zIndex: 999,
          }}
        >
          <span className="fa fa-angle-up relative" id="btn-vibrate"></span>
        </button>
      )}
    </React.Fragment>
  );
}
